import React, { useEffect, useMemo, useState } from "react";
import countryList from "react-select-country-list";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  CheckboxDropdown,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { RangePicker } from "../../../components/detail";
import {
  getUserList,
  getUserListByPage,
  getUserListByPageWithSearch,
  getUserListCount,
  userSearchQuery,
} from "../../../services/users";
import { sendBroadcast } from "../../../services/broadcasts";
import { getDate } from "../../../utils/format";
import { capitalize, matches, newestFirst } from "../../../utils/list";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import "../ads/ad-form.css";
import "./broadcast.css";

const LANGUAGES = [
  "English",
  "Deutsch",
  "French",
  "Italiano",
  "Espanol",
  "Polski",
];

export const NO_FILTERS = {
  search: "",
  subscription: "all",
  gender: "all",
  status: "all",
  languages: [],
  countries: [],
  range: { startDate: "", endDate: "" },
};

/** Users matching the recipient filters. */
export function filterRecipients(users, filters) {
  const start = filters.range.startDate
    ? new Date(filters.range.startDate)
    : null;
  const end = filters.range.endDate ? new Date(filters.range.endDate) : null;
  return (users || []).filter((user) => {
    const createdAt = new Date(user.createdAt);
    createdAt.setHours(0, 0, 0, 0);
    return (
      (matches(user.name, filters.search) ||
        matches(user.email, filters.search)) &&
      (filters.subscription === "all" ||
        user.subscription === filters.subscription) &&
      (filters.gender === "all" ||
        user.gender?.toLowerCase() === filters.gender) &&
      (filters.status === "all" ||
        user.status === (filters.status === "active")) &&
      (filters.languages.length === 0 ||
        filters.languages.some((lang) =>
          user.language?.toLowerCase().includes(lang.toLowerCase())
        )) &&
      (filters.countries.length === 0 ||
        filters.countries.includes(user.country)) &&
      (!start || !end || (createdAt >= start && createdAt <= end))
    );
  });
}

/** Users per page (as returned by the paginated API). */
const PAGE_SIZE = 10;

const EMPTY_MESSAGE = { title: "", subject: "", type: "text", message: "" };

const Broadcast = () => {
  const { t } = useTranslation();
  const countries = useMemo(
    () =>
      countryList()
        .getData()
        .map((country) => country.label),
    []
  );

  // Recipients come from the paginated API (fast). Every user is only
  // loaded on demand, to filter them or select them all.
  const [allUsers, setAllUsers] = useState(null); // null: not loaded
  const [allStatus, setAllStatus] = useState("idle"); // idle | loading | error
  const fullList = allUsers !== null;
  const loadAllUsers = async () => {
    setAllStatus("loading");
    try {
      // Newest first, like the paginated list.
      setAllUsers(newestFirst(await getUserList()));
      setAllStatus("idle");
    } catch (error) {
      setAllStatus("error");
    }
  };

  const [message, setMessage] = useState(EMPTY_MESSAGE);
  const setField = (field) => (value) =>
    setMessage((current) => ({ ...current, [field]: value }));

  const [filters, setFilters] = useState(NO_FILTERS);
  const setFilter = (field) => (value) =>
    setFilters((current) => ({ ...current, [field]: value }));
  const filtered = useMemo(
    () => (fullList ? filterRecipients(allUsers, filters) : []),
    [fullList, allUsers, filters]
  );
  const pagination = usePagination(filtered, PAGE_SIZE, [filters]);

  // Paginated list (until every user is loaded), searched on the server.
  const [page, setPage] = useState(1);
  const [pageUsers, setPageUsers] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [total, setTotal] = useState(null);
  const [serverSearch, setServerSearch] = useState("");
  useEffect(() => {
    getUserListCount().then((count) =>
      setTotal(typeof count === "number" ? count : null)
    );
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setServerSearch(filters.search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);
  useEffect(() => {
    if (fullList) return undefined;
    let cancelled = false;
    setPageLoading(true);
    const query = userSearchQuery(serverSearch);
    (query
      ? getUserListByPageWithSearch(page, query)
      : getUserListByPage(page)
    ).then((data) => {
      if (cancelled) return;
      const list = Array.isArray(data) ? data : [];
      // Newest first, as on the users page.
      setPageUsers(query ? list : [...list].reverse());
      setPageLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fullList, page, serverSearch]);

  const rows = fullList ? pagination.pageItems : pageUsers;
  const loading = fullList ? false : pageLoading;

  // { _id, email } of the selected users, as sent to the API.
  const [selected, setSelected] = useState([]);
  const isSelected = (user) =>
    selected.some((item) => item.email === user.email);
  const toggleUser = (user) =>
    setSelected((current) =>
      isSelected(user)
        ? current.filter((item) => item.email !== user.email)
        : [...current, { _id: user._id, email: user.email }]
    );
  // "Select all" picks the active users among the filtered ones (every page
  // once all the users are loaded, else the current page).
  const selectable = (fullList ? filtered : pageUsers).filter(
    (user) => user.status === true
  );
  const allSelected =
    selectable.length > 0 && selectable.every((user) => isSelected(user));
  const toggleAll = () =>
    setSelected((current) =>
      allSelected
        ? current.filter(
            (item) => !selectable.some((u) => u.email === item.email)
          )
        : [
            ...current,
            ...selectable
              .filter(
                (user) => !current.some((item) => item.email === user.email)
              )
              .map((user) => ({ _id: user._id, email: user.email })),
          ]
    );

  const [sending, setSending] = useState(false);
  const reset = () => {
    setMessage(EMPTY_MESSAGE);
    setSelected([]);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!message.message || !message.subject) {
      toast.error(t("Please Fill Broadcast Data Completely"));
      return;
    }
    setSending(true);
    try {
      const success = await sendBroadcast({
        title: message.title,
        message: message.message,
        users: selected,
        subject: message.subject,
        attachment: null,
        type: message.type,
      });
      if (success) {
        toast.success(t("Broadcast Sent Successfully!"));
        reset();
      } else {
        toast.error(t("Broadcast Failed!"));
      }
    } catch (error) {
      toast.error(t("Broadcast Failed!"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("New Broadcast")}
        subtitle={t("Send a message to the selected users")}
      />

      <form className="ui-card broadcast-card" onSubmit={handleSend}>
        <div className="ui-form broadcast-body">
          <div className="ui-field">
            <label htmlFor="broadcast-title">{t("Title")}</label>
            <input
              id="broadcast-title"
              className="ui-input"
              value={message.title}
              onChange={(e) => setField("title")(e.target.value)}
            />
          </div>
          <div className="ui-field">
            <label htmlFor="broadcast-subject">{t("Subject")}</label>
            <input
              id="broadcast-subject"
              className="ui-input"
              value={message.subject}
              onChange={(e) => setField("subject")(e.target.value)}
            />
          </div>
          <div className="ui-field ui-field-full">
            <div className="broadcast-message-head">
              <span className="ui-label">{t("Message")}</span>
              <Segmented
                label={t("Message type")}
                value={message.type}
                onChange={(type) =>
                  setMessage((current) => ({ ...current, type, message: "" }))
                }
                options={[
                  { value: "text", label: t("Text") },
                  { value: "html", label: "HTML" },
                ]}
              />
            </div>
            {message.type === "html" ? (
              <textarea
                className="ui-input broadcast-html"
                value={message.message}
                placeholder="<html>…</html>"
                aria-label={t("Message (HTML PAGE)")}
                onChange={(e) => setField("message")(e.target.value)}
              />
            ) : (
              <ReactQuill
                className="ui-editor"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                value={message.message}
                onChange={setField("message")}
              />
            )}
          </div>
        </div>
        <div className="broadcast-footer">
          <span className="broadcast-count">
            <i className="ri-group-line" aria-hidden="true"></i>
            {t("recipientCount", { count: selected.length })}
          </span>
          <div className="broadcast-actions">
            <button
              type="button"
              className="ui-btn ui-btn-secondary"
              onClick={reset}
            >
              {t("cancel")}
            </button>
            <button type="submit" className="ui-btn" disabled={sending}>
              {sending ? (
                <span className="ad-spinner" aria-hidden="true" />
              ) : (
                <i className="ri-send-plane-line" aria-hidden="true"></i>
              )}
              {t("Send")}
            </button>
          </div>
        </div>
      </form>

      <section className="ui-card">
        <div className="ui-toolbar">
          <h2 className="broadcast-title">{t("Recipients")}</h2>
          <SearchInput
            value={filters.search}
            onChange={setFilter("search")}
            placeholder={t("Search by name or email")}
          />
        </div>
        {!fullList && (
          <div className="broadcast-full" role="status">
            <i className="ri-filter-3-line" aria-hidden="true"></i>
            <span>
              {allStatus === "error"
                ? t("Loading every user failed.")
                : t(
                    "To filter by subscription, gender, country, language or date, or to select every user, load the full list."
                  )}
            </span>
            <button
              type="button"
              className="ui-btn ui-btn-secondary"
              disabled={allStatus === "loading"}
              onClick={loadAllUsers}
            >
              {allStatus === "loading" ? (
                <span className="ui-spinner" aria-hidden="true" />
              ) : (
                <i
                  className={
                    allStatus === "error"
                      ? "ri-refresh-line"
                      : "ri-download-2-line"
                  }
                  aria-hidden="true"
                ></i>
              )}
              {allStatus === "loading"
                ? t("Loading every user…")
                : allStatus === "error"
                ? t("Retry")
                : t("Load every user")}
            </button>
          </div>
        )}
        {fullList && (
          <div className="broadcast-filters">
            <select
              className="ui-input"
              aria-label={t("Subscription")}
              value={filters.subscription}
              onChange={(e) => setFilter("subscription")(e.target.value)}
            >
              <option value="all">{t("All subscriptions")}</option>
              <option value="paid">{t("Paid")}</option>
              <option value="free">{t("Free")}</option>
            </select>
            <select
              className="ui-input"
              aria-label={t("gender")}
              value={filters.gender}
              onChange={(e) => setFilter("gender")(e.target.value)}
            >
              <option value="all">{t("All genders")}</option>
              <option value="male">{t("Male")}</option>
              <option value="female">{t("Female")}</option>
            </select>
            <select
              className="ui-input"
              aria-label={t("status")}
              value={filters.status}
              onChange={(e) => setFilter("status")(e.target.value)}
            >
              <option value="all">{t("All statuses")}</option>
              <option value="active">{t("Active")}</option>
              <option value="inactive">{t("Inactive")}</option>
            </select>
            <CheckboxDropdown
              label={t("lang")}
              options={LANGUAGES}
              value={filters.languages}
              onChange={setFilter("languages")}
            />
            <CheckboxDropdown
              label={t("country")}
              options={countries}
              value={filters.countries}
              onChange={setFilter("countries")}
              searchable
            />
            <RangePicker
              range={filters.range}
              onChange={setFilter("range")}
              emptyLabel={t("Registration date")}
            />
          </div>
        )}

        {loading ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState icon="ri-user-search-line">
            {t("No Data Found")}
          </EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        aria-label={
                          fullList
                            ? t("Select all active users")
                            : t("Select the active users of this page")
                        }
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>{t("name")}</th>
                    <th>{t("Reg Date")}</th>
                    <th>{t("country")}</th>
                    <th>{t("gender")}</th>
                    <th>{t("package")}</th>
                    <th>{t("lang")}</th>
                    <th>{t("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((user, index) => (
                    <tr key={user._id || index}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={user.email}
                          checked={isSelected(user)}
                          onChange={() => toggleUser(user)}
                        />
                      </td>
                      <td>
                        <span className="ui-media-title">
                          {capitalize(user.name) || "N/A"}
                        </span>
                        <span className="ui-media-meta">
                          {user.email || "N/A"}
                        </span>
                      </td>
                      <td className="ui-cell-muted">
                        {user.createdAt ? getDate(user.createdAt) : "N/A"}
                      </td>
                      <td>{user.country || "N/A"}</td>
                      <td>{capitalize(user.gender) || "N/A"}</td>
                      <td>{capitalize(user.subscription) || "N/A"}</td>
                      <td>{capitalize(user.language) || "N/A"}</td>
                      <td>
                        {typeof user.status === "boolean" ? (
                          <StatusBadge active={user.status} />
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {fullList ? (
              <Pagination {...pagination} onChange={pagination.setPage} />
            ) : (
              <Pagination
                page={page}
                onChange={setPage}
                pageSize={PAGE_SIZE}
                {...(userSearchQuery(serverSearch) || total === null
                  ? { hasNext: pageUsers.length === PAGE_SIZE }
                  : {
                      pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
                      total,
                      hasNext: page < Math.ceil(total / PAGE_SIZE),
                    })}
              />
            )}
          </>
        )}
      </section>
    </Page>
  );
};

export default Broadcast;
